import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Pagination';
import { fmtDateTime } from '../../../utils/region';
import { settleAccountsApi, SA_ROLES, SaAccount, SaLogPage, SaMap, SaRole, SaRoleRow } from '../../../utils/api/settleAccounts';
import type { AwCtx } from '../ctx';
import { btn, card, errBox, inp, note, pillAmber, pillGreen, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, emptyLine, field, lbl } from '../dialog';
import { isReadOnlySeat } from './fixedAssets/shared';
import { AutoPostCard } from '../autoPost';

// ── Settings › Posting accounts (batch 11 R4; design §2.6, §4 R4; the W1 brief §3 P4 task 7) ──────────────────────────
// Which account of THIS client's chart holds the withholding tax a settlement posts: what the client withheld from a
// supplier and still owes the Revenue Department (wht_payable, ภ.ง.ด.3 / ภ.ง.ด.53), and what a customer withheld from
// the client (wht_prepaid). Two role cards, "answered n / 2", and the answer log below them, 100 a page.
// ⛔ NOTHING IS PREFILLED OR SUGGESTED: a client starts at 0 / 2 and only a person's choice changes that. Each select
// lists exactly the server's `eligible` accounts for its role (services/settleAccounts.ts — live, switched on, postable,
// not a control account, not cash, of the role's class), so the screen never offers what a save would refuse; an
// answer that has stopped being eligible since is drawn amber with the server's rule, and is not counted.
// ⛔ A select saves when it changes; the screen then reads the whole body and the log again, so the lists never go
// stale under the next choice. "+ New account" is TYPED, so it opens full screen with `dirty` (X, Esc, the backdrop and
// Cancel ask once — the shell's question) and its Save pinned in the shell's Foot; it waits for a code, a Thai name, a
// code this chart does not already use and a cash-flow section (never 'cash' — the server's list arrives without it).
// ⛔ A read-only seat (books / auditor) sees the answers and the log and no write control at all — never one shown
// disabled. The menu already hides this screen from a books login (menus.ts staffOnly), and the server refuses that
// login the whole mount; the rule holds here as well.
// ⛔ A client with no chart gets one line and nothing to press.

type TF = (key: string, opts?: Record<string, unknown>) => string;
const amberBox: React.CSSProperties = { border: '1px solid #F1C232', background: '#FFFBEB' };

/** The server's refusal as a sentence of this screen: every code the route answers has its own words. */
const paccErr = (e: any, t: TF): string => {
  if (e?.readOnlySeat) return String(e.message || '');
  const p = e?.payload || {};
  const code = String(p.code || '');
  const k = `aw_pacc_err_${code}`;
  if (code && t(k) !== k) return t(k, { why: ruleText(t, p.rule) });
  return String(e?.message || '') || t('aw_pacc_err_generic');
};
const ruleText = (t: TF, rule: any): string => {
  const k = `aw_pacc_rule_${String(rule || '')}`;
  return t(k) !== k ? t(k) : t('aw_pacc_rule_other');
};
const whoWhen = (name: string, at: number): string => (!name ? '—' : at ? `${name} · ${fmtDateTime(at)}` : name);
function useAcctName() {
  const { i18n } = useTranslation();
  const thai = i18n.language === 'th';
  return (a: SaAccount | null | undefined): string => (a ? `${a.code} · ${thai ? a.name_th || a.name_en : a.name_en || a.name_th}` : '');
}

export function PostingAccountsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const readOnly = isReadOnlySeat(ctx);
  const acctName = useAcctName();
  const [s, setS] = useState<SaMap | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [busyRole, setBusyRole] = useState('');
  const [newFor, setNewFor] = useState<SaRole | null>(null);
  const [logPage, setLogPage] = useState(1);
  const [log, setLog] = useState<SaLogPage | null>(null);
  useEffect(() => { setErr(''); setLogPage(1); }, [ctx.clientId]);
  useEffect(() => {
    let alive = true;
    settleAccountsApi.get(ctx.clientId).then(d => { if (alive) { setS(d); setLoadErr(''); } }).catch((e: any) => { if (alive) setLoadErr(paccErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    settleAccountsApi.log(ctx.clientId, logPage).then(d => { if (alive) setLog(d); }).catch((e: any) => { if (alive) setLoadErr(paccErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, reload, logPage]);   // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => setReload(n => n + 1);
  const pick = async (role: SaRole, accountId: string) => {
    if (busyRole) return;
    setBusyRole(role); setErr('');
    try { await settleAccountsApi.put(ctx.clientId, role, accountId); } catch (e: any) { setErr(paccErr(e, t)); } finally { setBusyRole(''); refresh(); }
  };

  if (!s) return loadErr ? <div style={errBox} data-pacc-load-error>{loadErr}</div> : <Loader />;
  const done = s.answered >= s.total;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-pane="posting-accounts">
      <section style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ ...sectionHead, marginBottom: 0, flex: 1 }}>{t('aw_pacc_title')}</div>
          {s.hasChart && (
            <span style={done ? pillGreen : pillAmber} data-pacc-answered={s.answered}>{t('aw_pacc_answered', { n: s.answered, total: s.total })}</span>
          )}
        </div>
        <div style={{ ...note, marginTop: 6 }}>{t('aw_pacc_intro')}</div>
      </section>
      {loadErr && <div style={errBox} data-pacc-load-error>{loadErr}</div>}
      {err && <div style={errBox} data-pacc-error>{err}</div>}
      {!s.hasChart ? (
        <div style={{ ...card, fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 8 }} data-pacc-no-chart>
          <i className="ti ti-info-circle" aria-hidden style={{ color: '#92400E', marginTop: 2 }} /><span>{t('aw_pacc_no_chart')}</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12 }}>
          {SA_ROLES.map(role => s.rows.find(r => r.role === role)).filter(Boolean).map(r => (
            <RoleCard key={(r as SaRoleRow).role} r={r as SaRoleRow} readOnly={readOnly} busy={!!busyRole} spinning={busyRole === (r as SaRoleRow).role}
              acctName={acctName} t={t} onPick={pick} onNew={role => setNewFor(role)} />
          ))}
        </div>
      )}
      {/* Automatic posting (owner 2026-11-02): its own switch and trail, in ../autoPost.tsx. Asked of a client with a chart: without one nothing could be generated. */}
      {s.hasChart && <AutoPostCard ctx={ctx} />}
      <section style={card} data-pacc-log>
        <div style={sectionHead}>{t('aw_pacc_log_title')}</div>
        {!log ? <Loader /> : !log.rows.length ? <div style={emptyLine} data-pacc-log-empty>{t('aw_pacc_log_empty')}</div> : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={table}>
                <thead><tr>
                  <th style={th}>{t('aw_pacc_log_col_at')}</th><th style={th}>{t('aw_pacc_log_col_role')}</th>
                  <th style={th}>{t('aw_pacc_log_col_from')}</th><th style={th}>{t('aw_pacc_log_col_to')}</th><th style={th}>{t('aw_pacc_log_col_by')}</th>
                </tr></thead>
                <tbody>
                  {log.rows.map(x => (
                    <tr key={x.id} data-pacc-log-row={x.id} data-role={x.role}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(x.at)}</td>
                      <td style={td}>{(SA_ROLES as readonly string[]).includes(x.role) ? t(`aw_pacc_role_${x.role}`) : x.role}</td>
                      <td style={td}>{x.oldAccount ? acctName(x.oldAccount) : x.oldAccountId ? t('aw_pacc_account_unknown') : t('aw_pacc_not_answered')}</td>
                      <td style={td} data-pacc-log-to>{x.newAccount ? acctName(x.newAccount) : x.newAccountId ? t('aw_pacc_account_unknown') : t('aw_pacc_not_answered')}</td>
                      <td style={td}>{x.actorName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={log.page} total={log.total} limit={log.limit || 100} onPage={setLogPage} />
          </>
        )}
      </section>
      {newFor && !readOnly && s.hasChart && (
        <NewAccountDialog ctx={ctx} role={newFor} codesInUse={s.codesInUse} cfSections={s.cfSections}
          onClose={() => setNewFor(null)} onCreated={() => { setNewFor(null); setErr(''); setLogPage(1); refresh(); }} />
      )}
    </div>
  );
}

// ── One role ────────────────────────────────────────────────────────────────────────────────────────────────────────
function RoleCard({ r, readOnly, busy, spinning, acctName, t, onPick, onNew }: {
  r: SaRoleRow; readOnly: boolean; busy: boolean; spinning: boolean; acctName: (a: SaAccount | null | undefined) => string; t: TF;
  onPick: (role: SaRole, accountId: string) => void; onNew: (role: SaRole) => void;
}) {
  const off = !r.accountId || !!r.problem;
  return (
    <section style={{ ...card, ...(off ? amberBox : {}) }} data-pacc-role={r.role}>
      <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t(`aw_pacc_role_${r.role}`)}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{t(`aw_pacc_role_hint_${r.role}`)}</div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{r.classes.map(c => t(`pa_chart_class_${c}`)).join(' / ')}</div>
      <div style={{ marginTop: 10 }}>
        {readOnly ? (
          <span style={{ fontSize: 12.5 }} data-pacc-value={r.role}>{r.account ? acctName(r.account) : t('aw_pacc_not_answered')}</span>
        ) : (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={r.accountId} disabled={busy} data-pacc-select={r.role} aria-label={t(`aw_pacc_role_${r.role}`)}
              onChange={e => onPick(r.role, e.target.value)} style={{ ...inp, minWidth: 260, maxWidth: 420 }}>
              <option value="">{t('aw_pacc_pick')}</option>
              {r.eligible.map(a => <option key={a.id} value={a.id}>{acctName(a)}</option>)}
              {/* A stored answer that is no longer eligible stays visible as what it is — the rule below says why. */}
              {r.accountId && !r.eligible.some(a => a.id === r.accountId) && <option value={r.accountId}>{r.account ? acctName(r.account) : t('aw_pacc_account_unknown')}</option>}
            </select>
            {spinning && <i className="ti ti-loader" aria-hidden />}
            <button style={btn} onClick={() => onNew(r.role)} disabled={busy} data-pacc-new={r.role}>
              <i className="ti ti-plus" aria-hidden /> {t('aw_pacc_new_account')}
            </button>
          </div>
        )}
        {r.problem && <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 4 }} data-pacc-problem={r.role} data-rule={r.problem.rule}>{ruleText(t, r.problem.rule)}</div>}
        {!r.accountId && <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 4 }} data-pacc-missing={r.role}>{t('aw_pacc_not_answered')}</div>}
        {!!r.setByName && <div style={{ ...note, marginTop: 4 }} data-pacc-who={r.role}>{whoWhen(r.setByName, r.setAt)}</div>}
      </div>
    </section>
  );
}

// ── "+ New account" (full screen, a form) ───────────────────────────────────────────────────────────────────────────
// The class is the role's and is shown locked; the code and the names are the person's — nothing is suggested or
// prefilled. One POST makes the chart row through accountingSetup.addAccount (logged in coa_change_log under this
// user) and answers the role with it, in one transaction. Both classes are balance-sheet classes: the cash-flow section
// is required, and the server's list of sections arrives without 'cash'. No working-account section (income and
// expense only).
function NewAccountDialog({ ctx, role, codesInUse, cfSections, onClose, onCreated }: {
  ctx: AwCtx; role: SaRole; codesInUse: string[]; cfSections: string[]; onClose: () => void; onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [nameTh, setNameTh] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [cfSection, setCfSection] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const taken = useMemo(() => new Set((codesInUse || []).map(c => String(c).trim())), [codesInUse]);
  const sections = (cfSections || []).filter(v => v !== 'cash');
  const codeTaken = !!code.trim() && taken.has(code.trim());
  const ready = !!code.trim() && !!nameTh.trim() && !codeTaken && sections.includes(cfSection);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await settleAccountsApi.create(ctx.clientId, role, {
        code: code.trim(), nameTh: nameTh.trim(), ...(nameEn.trim() ? { nameEn: nameEn.trim() } : {}), cfSection,
      });
      setDirty(false); onCreated();
    } catch (e: any) { setErr(paccErr(e, t)); } finally { setBusy(false); }
  };
  const cls = role === 'wht_prepaid' ? 'asset' : 'liability';
  return (
    <Dialog fullscreen title={t('aw_pacc_na_title')} docNo={t(`aw_pacc_role_${role}`)} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-plus" label={t('aw_pacc_na_save')} t={t} />}>
      <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 12 }} data-pacc-new-form={role}>
        <div style={note}>{t('aw_pacc_na_note')}</div>
        {err && <div style={errBox} data-pacc-na-error>{err}</div>}
        <div>
          <label style={lbl}>{t('aw_pacc_na_class')}</label>
          <input value={t(`pa_chart_class_${cls}`)} readOnly tabIndex={-1} style={{ ...field, color: 'var(--text-secondary)', background: 'var(--bg-app)' }} data-pacc-na="class" data-class={cls} />
        </div>
        <div>
          <label htmlFor="pacc-na-code" style={lbl}>{t('aw_pacc_na_code')}</label>
          <input id="pacc-na-code" value={code} onChange={e => ed(setCode, e.target.value)} style={{ ...field, ...(codeTaken ? amberBox : {}) }} data-pacc-na="code" />
          {codeTaken && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }} data-pacc-na-taken>{t('aw_pacc_na_code_taken', { code: code.trim() })}</div>}
        </div>
        <div>
          <label htmlFor="pacc-na-th" style={lbl}>{t('aw_pacc_na_name_th')}</label>
          <input id="pacc-na-th" value={nameTh} onChange={e => ed(setNameTh, e.target.value)} style={field} data-pacc-na="name_th" />
        </div>
        <div>
          <label htmlFor="pacc-na-en" style={lbl}>{t('aw_pacc_na_name_en')}</label>
          <input id="pacc-na-en" value={nameEn} onChange={e => ed(setNameEn, e.target.value)} style={field} data-pacc-na="name_en" />
        </div>
        <div>
          <label htmlFor="pacc-na-cf" style={lbl}>{t('aw_pacc_na_cf')}<span aria-hidden style={{ color: '#B42318', marginLeft: 3 }}>*</span></label>
          <select id="pacc-na-cf" value={cfSection} onChange={e => ed(setCfSection, e.target.value)} style={{ ...field, ...(!cfSection ? amberBox : {}) }} data-pacc-na="cf">
            <option value="">{t('aw_pacc_na_cf_pick')}</option>
            {sections.map(v => <option key={v} value={v}>{t(`pa_chart_cf_${v}`)}</option>)}
          </select>
          <div style={{ ...note, marginTop: 3 }}>{t('aw_pacc_na_cf_needed')}</div>
        </div>
      </div>
    </Dialog>
  );
}
